'use client'

import type { RtLegIndex } from '@/src/types/srpJourneyIndex'
import { createContext, type ReactNode, useContext } from 'react'

/**
 * Ambient RT leg (0 = onward, 1 = return) for the current render subtree.
 *
 * In RT split view both legs render at the same time, so the "active leg" can
 * no longer be a single global value. Each leg column wraps its subtree in
 * <RtLegProvider leg={n}> and `useSearchResults()` resolves its leg from here,
 * which makes every existing reader/writer target the correct leg automatically.
 *
 * Default 0 keeps every existing call site (OW, multicity, solution view, and
 * any hook rendered outside a column) behaving exactly as before.
 */
const RtLegContext = createContext<RtLegIndex>(0)

interface RtLegProviderProps {
    children: ReactNode
    leg: RtLegIndex
}

export const RtLegProvider = ({ children, leg }: RtLegProviderProps) => (
    <RtLegContext.Provider value={leg}>{children}</RtLegContext.Provider>
)

export const useRtLeg = (): RtLegIndex => useContext(RtLegContext)

export { RtLegContext }
